import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  RegistrarCreateSchema,
  RegistrarQueryAllSchema,
  RegistrarQuerySchema,
  RegistrarUpdateSchema,
} from './schemas';

export function useGetRegistrars(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['REGISTRARS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RegistrarQueryAllSchema>({
        endpoint: '/api/v1/registrars',
        params: `${apiParamStr}&include=logo,domainsCount`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['REGISTRARS'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, RegistrarCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/registrars/',
        payload,
      }),
  });

export const useGetRegistrarsMutation: UseControlledMutation<RegistrarQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['REGISTRARS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<RegistrarQueryAllSchema>({
            endpoint: '/api/v1/registrars',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetRegistrar(id: string) {
  return useSuspenseQuery({
    queryKey: ['REGISTRAR', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RegistrarQuerySchema }>({
        endpoint: `/api/v1/registrars/${id}?include=${phoneNumberIncludeKey()},logo,domains`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: RegistrarQuerySchema }, ApiError, Partial<RegistrarUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/registrars/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['REGISTRAR_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/registrars/${id}`,
        method: 'DELETE',
      }),
  });
